import React, {Component} from 'react'
import {getResource, updateResource} from '../../../data/actions/resource'
import LocalStorage from '../../../util/localStorage'
import {Field, FieldsManager} from '../../../data/services/fields'
import Resources from '../../../data/services/resources'
import {UPDATE_PERM} from '../../../util/util-constants'
import {checkPerm, classNames, getProp} from '../../../common/util/util-helpers'
import {excludeFields, fieldsToHtml, fillFieldsFromData, includeFields} from "../../../common/util/util-fields";
import Page from "../../../common/components/layout/layout-components/page";
import Card from "../../../common/components/card";
import {LoaderSmall} from "../../../common/components/loader";
import PageFooter from "../../../common/components/layout/layout-components/page/page-footer";
import DriverPayrollSettings from "../driver-payroll/dialogs/driver-payroll-settings";
import {scrollErrorIntoView} from "../../../common/util/util-vanilla";
import DriverPayrollOverrides from "../driver-payroll/dialogs/payroll-overrides";

class PayrollSettingsTab extends Component {
    constructor(props) {
        super(props)
        this.state = {
            fields: this.getFields(),
            canSubmit: false,
        }
    }

    /** Lifecycle
     ================================================================= */
    componentDidMount = () => {
        this.fetchData()
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps.resource.isLoading && (!this.props.resource.isLoading && !!this.props.resource.data)) {
            const item = getProp(this.props, 'resource.data', {})
            this.setState({
                fields: this.getFields(item),
            })
        }

        if (this.props.setIsTabDirty && this.state.canSubmit !== prevState.canSubmit) {
            this.props.setIsTabDirty(this.state.canSubmit);
        }
    }

    /** Data Events
     ================================================================= */
    fetchData = () => {
        this.props.dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: Resources.PayrollSettingsGeneral
        }))
    }

    onSubmit = () => {
        if (!this.state.canSubmit) {
            return
        }

        this.setState({fields: FieldsManager.validateFields(this.state.fields)}, () => {
            if (FieldsManager.checkFieldsForErrors(this.state.fields)) {
                this.setState({canSubmit: false}, () => {
                    this.props.dispatch(updateResource({
                        user: LocalStorage.get('user'),
                        params: Object.assign({}, FieldsManager.getFieldKeyValues(this.state.fields)),
                        resource: Resources.PayrollSettingsGeneral,
                        piggyResource: Resources.PayrollSettingsGeneral,
                        errorMessage: true, successMessage: 'Settings saved successfully!'
                    }))
                })
            } else {
                scrollErrorIntoView(this.state.fields)
            }
        })
    }

    /** UI Events
     ================================================================= */

    handleInputChange = (name, value) => {
        const newFields = FieldsManager.updateField(this.state.fields, name, value)

        this.setState({
            canSubmit: true,
            fields: newFields
        })
    }

    onCancel = () => {
        if (!this.state.canSubmit) {
            return
        }

        this.setState({
            canSubmit: false,
            fields: this.getFields(getProp(this.props, 'resource.data', {}))
        })
    }

    /** Helpers
     ================================================================= */
    getFields = (item = {}) => {
        const fieldTemplates = {
            ShowDispatchPay: new Field('ShowDispatchPay', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            HidePayrollBalances: new Field('HidePayrollBalances', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowNegativePayCheck: new Field('ShowNegativePayCheck', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowDetailedDeductions: new Field('ShowDetailedDeductions', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            SplitPositiveNegativeOnDetailed: new Field('SplitPositiveNegativeOnDetailed', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            HideDispatcher: new Field('HideDispatcher', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            HideYTD: new Field('HideYTD', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowDriverAddress: new Field('ShowDriverAddress', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            SplitTollsOnSettlement: new Field('SplitTollsOnSettlement', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            SplitFuelOnSettlement: new Field('SplitFuelOnSettlement', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowTrucksForDrivers: new Field('ShowTrucksForDrivers', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowMileage: new Field('ShowMileage', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            ShowRateType: new Field('ShowRateType', '', [''], false, 'checkbox', {addContainerClass: 'col-span-full'}),
            DispatchPayDateDefault: new Field('DispatchPayDateDefault', '', ['empty'], false, 'select', {
                addContainerClass: 'col-span-full pt-6'
            }, {
                values: {
                    '1': 'Pickup Date',
                    '2': 'Delivery Date',
                    '3': 'Delivery Time Out'
                }
            }),

            SettlementNotes: new Field('SettlementNotes', '', [], false, 'rich-text', {
                addContainerClass: 'col-span-full',
                hideLabel: true
            }, {className: "form-control max-w-full min-h-[10rem]"}),
        }

        return fillFieldsFromData(fieldTemplates, item)
    }

    /** Render
     ================================================================= */
    render() {
        const {translate} = this.props
        const isLoading = this.props.resource.isLoading


        const info = fieldsToHtml(Object.values(excludeFields(this.state.fields, ['SettlementNotes'])), translate, this.handleInputChange)
        const settlementNotes = fieldsToHtml(Object.values(includeFields(this.state.fields, ['SettlementNotes'])), translate, this.handleInputChange)

        return (
            <Page>
                <div className="relative">
                    <div className={classNames('mt-6 grid gap-4 grid-cols-6', isLoading ? 'invisible' : '')}>
                        <Card addClass="col-span-6">
                            <h5 className="text-tm-gray-700 text-base">{translate('page.title.payroll')}</h5>

                            <div className="grid grid-cols-3">
                                {info}
                            </div>
                        </Card>

                        <Card addClass="col-span-6">
                            <h5 className="text-tm-gray-700 text-base">{translate('text.settlement_notes')}</h5>
                            <div className="mb-4 text-sm text-tm-gray-500">{translate('text.settlement_notes_note')}</div>

                            <div className="grid grid-cols-1">
                                {settlementNotes}
                            </div>
                        </Card>

                        <Card addClass="col-span-6">
                            <h5 className="mb-4 text-tm-gray-700 text-base">{translate('text.payroll_overrides')}</h5>

                            <DriverPayrollOverrides
                                location={this.props.location}
                                translate={translate}
                                dispatch={this.props.dispatch}
                                secondResource={this.props.secondResource}
                            />
                        </Card>

                        <Card addClass="col-span-6 mb-20">
                            <h5 className="mb-4 text-tm-gray-700 text-base">{translate('text.accounts')}</h5>

                            <DriverPayrollSettings
                                location={this.props.location}
                                translate={translate}
                                dispatch={this.props.dispatch}
                                thirdResource={this.props.thirdResource}
                            />
                        </Card>

                    </div>
                    {!!isLoading && (
                        <div className={'inset-0'}>
                            <div className="inset-center">
                                <LoaderSmall/>
                            </div>
                        </div>
                    )}

                    {!isLoading && (
                        <PageFooter
                            translate={translate}
                            canSubmit={this.state.canSubmit && checkPerm(Resources.AccountingSettings, UPDATE_PERM)}
                            actionCancel={this.onCancel}
                            actionSubmit={this.onSubmit}
                        />
                    )}
                </div>
            </Page>
        )
    }
}

export default PayrollSettingsTab
